import { Link } from 'react-router-dom';
// Social media icons - uncomment when social media accounts are set up
// import { FaFacebook, FaInstagram, FaXTwitter, FaTiktok } from 'react-icons/fa6';
import { GiGardeningShears, GiFeather } from 'react-icons/gi';
import { MdCleaningServices, MdPrivacyTip, MdGavel, MdAccessibility } from 'react-icons/md';
import { IoDocumentText, IoShieldCheckmark } from 'react-icons/io5';
import { HiMail, HiLocationMarker } from 'react-icons/hi';
import { ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SITE_CONFIG } from '@/lib/constants';

const serviceLinks = [
  { href: '/services/gardening', label: 'Gardening', icon: GiGardeningShears },
  { href: '/services/cleaning', label: 'Cleaning', icon: MdCleaningServices },
  { href: '/services/taxidermy', label: 'Taxidermy', icon: GiFeather },
];

const legalLinks = [
  { href: '/legal/privacy', label: 'Privacy Policy', icon: MdPrivacyTip },
  { href: '/legal/terms', label: 'Terms of Service', icon: MdGavel },
  { href: '/legal/disclaimer', label: 'Disclaimer', icon: IoDocumentText },
  { href: '/legal/shipping', label: 'Shipping Policy', icon: IoShieldCheckmark },
  { href: '/legal/accessibility', label: 'Accessibility', icon: MdAccessibility },
];

/**
 * Footer component with navigation, social icons, and Buun Group credit
 */
export function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t border-border bg-card" role="contentinfo">
      <div className="container-custom py-12 md:py-16">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand Column */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              <img
                src="/logo/logo-icon.png"
                alt="Cloud9 Logo"
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-sm text-muted-foreground">
              {SITE_CONFIG.tagline}
            </p>
            <a
              href="https://abr.business.gov.au/ABN/View?id=36695283041"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              ABN: 36 695 283 041
            </a>
            {/* Social Icons - uncomment when social media accounts are set up
            <div className="flex items-center gap-3 pt-2">
              <a
                href="#"
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/50 text-muted-foreground transition-all duration-300 hover:bg-[#1877F2] hover:text-white hover:scale-110"
                aria-label="Facebook"
              >
                <FaFacebook className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="#"
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/50 text-muted-foreground transition-all duration-300 hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#FD1D1D] hover:to-[#F77737] hover:text-white hover:scale-110"
                aria-label="Instagram"
              >
                <FaInstagram className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="#"
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/50 text-muted-foreground transition-all duration-300 hover:bg-foreground hover:text-background hover:scale-110"
                aria-label="X (Twitter)"
              >
                <FaXTwitter className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="#"
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/50 text-muted-foreground transition-all duration-300 hover:bg-foreground hover:text-background hover:scale-110"
                aria-label="TikTok"
              >
                <FaTiktok className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
            */}
          </div>

          {/* Services Column */}
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-foreground">
              Services
            </h3>
            <ul className="space-y-2" role="list">
              {serviceLinks.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    <item.icon className="h-4 w-4 transition-transform group-hover:scale-110" aria-hidden="true" />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Column */}
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-foreground">
              Legal
            </h3>
            <ul className="space-y-2" role="list">
              {legalLinks.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    <item.icon className="h-4 w-4 transition-transform group-hover:scale-110" aria-hidden="true" />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-foreground">
              Contact
            </h3>
            <ul className="space-y-3" role="list">
              <li>
                <a
                  href={`mailto:${SITE_CONFIG.contact.email}`}
                  className="group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  <HiMail className="h-4 w-4 transition-transform group-hover:scale-110" aria-hidden="true" />
                  {SITE_CONFIG.contact.email}
                </a>
              </li>
              <li className="group flex items-center gap-2 text-sm text-muted-foreground">
                <HiLocationMarker className="h-4 w-4" aria-hidden="true" />
                {SITE_CONFIG.contact.location}
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-border pt-6 sm:flex-row">
          <div className="flex flex-col sm:flex-row items-center gap-2 text-sm text-muted-foreground">
            <span>&copy; {currentYear} {SITE_CONFIG.name}. All rights reserved.</span>
            <span className="hidden sm:inline">·</span>
            <span className="flex items-center gap-1.5">
              Built and designed by
              <a
                href="https://buungroup.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-primary transition-colors"
              >
                <img
                  src="https://buungroup.com/logo/logo.svg"
                  alt="Buun Group Logo"
                  className="h-4 w-4 spin-on-hover"
                />
                <span className="font-medium">Buun Group</span>
              </a>
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={scrollToTop}
            className="gap-2"
            aria-label="Scroll to top"
          >
            <ArrowUp className="h-4 w-4" aria-hidden="true" />
            Back to top
          </Button>
        </div>
      </div>
    </footer>
  );
}
